import React from "react";
import {
    Paperclip, Download, Trash2, FileIcon, Film, Image, Link2, ExternalLink,
} from "lucide-react";
import { EmptyState } from "../shared/EmptyState";
import * as api from "../../lib/api";
import { isLinkDoc, LinkProviderBadge, linkHost } from "../shared/LinkDocDialog";

function formatFileSize(bytes) {
    if (bytes < 1024) return `${bytes} o`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}

function Thumb({ att }) {
    if (isLinkDoc(att)) return <div className="w-16 h-16 rounded-md border bg-sky-50 flex items-center justify-center"><Link2 className="w-6 h-6 text-sky-500" /></div>;
    if (att.content_type?.startsWith("image/")) return <img src={api.getAttachmentUrl(att.id)} alt={att.filename} className="w-16 h-16 object-cover rounded-md border" />;
    if (att.content_type?.startsWith("video/")) return <div className="w-16 h-16 rounded-md border bg-purple-50 flex items-center justify-center"><Film className="w-6 h-6 text-purple-500" /></div>;
    if (att.content_type === "application/pdf") return <div className="w-16 h-16 rounded-md border bg-red-50 flex items-center justify-center"><FileIcon className="w-6 h-6 text-red-500" /></div>;
    return <div className="w-16 h-16 rounded-md border bg-gray-50 flex items-center justify-center"><FileIcon className="w-6 h-6 text-gray-400" /></div>;
}

export function TicketAttachments({ attachments, isAgentOrAdmin, getUserById, onDelete, onPreview, formatRelativeTime }) {
    if (!attachments || attachments.length === 0) {
        return (
            <EmptyState
                icon={Paperclip}
                title="Aucune pièce jointe"
                description="Ajoutez des fichiers, captures d'écran ou enregistrements depuis la zone de commentaire, ou un lien OneDrive / SharePoint via « Ajouter un lien »"
            />
        );
    }

    return (
        <div className="space-y-3">
            {attachments.map((att) => {
                const link = isLinkDoc(att);
                const author = getUserById(att.author_id);
                const authorName = author ? `${author.first_name} ${author.last_name}` : "Inconnu";
                return (
                    <div
                        key={att.id}
                        className="flex items-start gap-3 p-3 rounded-lg border hover:bg-gray-50 transition-colors cursor-pointer"
                        data-testid={`attachment-${att.id}`}
                        onClick={() => (link ? window.open(att.url, "_blank", "noopener") : onPreview(att))}
                    >
                        <div className="flex-shrink-0"><Thumb att={att} /></div>
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate flex items-center gap-2">{att.filename}{link && <LinkProviderBadge url={att.url} />}</p>
                            <p className="text-xs text-gray-500 truncate">
                                {link ? linkHost(att.url) : formatFileSize(att.size)} — {authorName} — {formatRelativeTime(att.created_at)}
                            </p>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                            {link ? (
                                <a href={att.url} target="_blank" rel="noreferrer noopener" className="p-1.5 text-gray-400 hover:text-sky-600 rounded-md hover:bg-sky-50 transition-colors" title="Ouvrir le lien" data-testid={`open-link-${att.id}`}>
                                    <ExternalLink className="w-4 h-4" />
                                </a>
                            ) : (
                                <a href={api.getAttachmentUrl(att.id)} download={att.filename} className="p-1.5 text-gray-400 hover:text-blue-600 rounded-md hover:bg-blue-50 transition-colors" title="Télécharger" data-testid={`download-${att.id}`}>
                                    <Download className="w-4 h-4" />
                                </a>
                            )}
                            {isAgentOrAdmin && (
                                <button type="button" onClick={() => onDelete(att.id)} className="p-1.5 text-gray-400 hover:text-red-600 rounded-md hover:bg-red-50 transition-colors" title="Supprimer" data-testid={`delete-att-${att.id}`}>
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
